/**
 * ProjectDetail Page
 *
 * Dashboard view for a development project (subdivision, townhome community, etc.)
 * Shows:
 * - Project overview with stats
 * - Units list with permit status
 * - Linked jobs (both unit-specific and shared)
 * - Segment management
 */

import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useProject, useProjectJobs } from '@/hooks/useProject';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import {
  ArrowLeft,
  Building2,
  CalendarDays,
  Home,
  MapPin,
  Plus,
  Users,
  Briefcase,
  Settings,
  Loader2,
  AlertCircle,
  CheckCircle,
  Clock,
  Grid3X3,
  FileText,
  Building,
  Landmark,
  LayoutGrid,
  Edit,
  Trash2,
  Phone,
  Mail,
  FolderOpen,
  Shield,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AddJobsWizard } from '@/components/project/AddJobsWizard';
import { BulkComplianceCheck } from '@/components/project/BulkComplianceCheck';
import { DevelopmentProcessSteps } from '@/components/project/DevelopmentProcessSteps';
import { NextBlockedItems } from '@/components/project/NextBlockedItems';
import { ProjectDocumentLibrary } from '@/components/project/ProjectDocumentLibrary';
import { ProjectStatsGrid } from '@/components/project/ProjectStatsGrid';
import { ProjectTimeline } from '@/components/project/ProjectTimeline';
import type {
  DevelopmentProjectType,
  DevelopmentProjectStatus,
  Unit,
  UnitType,
  UnitStatus,
  ProjectSegment,
  SegmentType,
  CreateUnitInput,
} from '@/types/project';

// Project type icons
const PROJECT_TYPE_ICONS: Record<DevelopmentProjectType, React.ElementType> = {
  townhome: Home,
  apartment: Building2,
  subdivision: Grid3X3,
  mixed_use: LayoutGrid,
  commercial_complex: Landmark,
  single_property: Building,
};

// Project type labels
const PROJECT_TYPE_LABELS: Record<DevelopmentProjectType, string> = {
  townhome: 'Townhome Community',
  apartment: 'Apartment Complex',
  subdivision: 'Subdivision',
  mixed_use: 'Mixed Use Development',
  commercial_complex: 'Commercial Complex',
  single_property: 'Single Property',
};

// Status badge colors
const STATUS_COLORS: Record<DevelopmentProjectStatus, string> = {
  planning: 'bg-gray-100 text-gray-800',
  permitting: 'bg-blue-100 text-blue-800',
  construction: 'bg-yellow-100 text-yellow-800',
  closeout: 'bg-purple-100 text-purple-800',
  complete: 'bg-green-100 text-green-800',
};

// Unit status badge colors
const UNIT_STATUS_COLORS: Record<UnitStatus, string> = {
  pending: 'bg-gray-100 text-gray-800',
  permitting: 'bg-blue-100 text-blue-800',
  construction: 'bg-yellow-100 text-yellow-800',
  complete: 'bg-green-100 text-green-800',
};

// Unit type labels
const UNIT_TYPE_LABELS: Record<UnitType, string> = {
  lot: 'Lot',
  unit: 'Unit',
  building: 'Building',
  townhome: 'Townhome',
  apartment: 'Apartment',
  space: 'Space',
};

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();

  // Project data
  const {
    project,
    segments,
    units,
    isLoading,
    error,
    updateProject,
    createUnit,
    batchCreateUnits,
    deleteUnit,
    createSegment,
    deleteSegment,
  } = useProject(id);

  // Project jobs
  const {
    jobs,
    unitJobs,
    sharedJobs,
    isLoading: jobsLoading,
    refresh: refreshJobs,
  } = useProjectJobs(id);

  // Local state
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddUnitDialog, setShowAddUnitDialog] = useState(false);
  const [showAddSegmentDialog, setShowAddSegmentDialog] = useState(false);
  const [showAddJobsWizard, setShowAddJobsWizard] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState<Unit | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // New unit form state
  const [newUnit, setNewUnit] = useState<CreateUnitInput>({
    label: '',
    unitType: 'unit',
    segmentId: undefined,
    description: '',
  });

  // New segment form state
  const [newSegment, setNewSegment] = useState({
    name: '',
    segmentType: 'phase' as SegmentType,
    description: '',
  });

  // Unit filters
  const [unitStatusFilter, setUnitStatusFilter] = useState<UnitStatus | 'all'>('all');
  const [unitSegmentFilter, setUnitSegmentFilter] = useState<string>('all');

  // Auth redirect
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/auth');
    }
  }, [navigate, user, authLoading]);

  // Computed stats
  const progressPercent = useMemo(() => {
    if (!project || !project.unitStats) return 0;
    const total = project.totalUnits || 1;
    const complete = project.unitStats.issued || 0;
    return Math.round((complete / total) * 100);
  }, [project]);

  // Filtered units
  const filteredUnits = useMemo(() => {
    return units.filter((unit) => {
      if (unitStatusFilter !== 'all' && unit.status !== unitStatusFilter) {
        return false;
      }
      if (unitSegmentFilter !== 'all' && unit.segmentId !== unitSegmentFilter) {
        return false;
      }
      return true;
    });
  }, [units, unitStatusFilter, unitSegmentFilter]);

  // Handle add unit
  const handleAddUnit = async () => {
    if (!newUnit.label.trim()) {
      toast({
        title: 'Error',
        description: 'Unit label is required',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await createUnit(newUnit);
      toast({
        title: 'Unit added',
        description: `${newUnit.label} has been added to the project`,
      });
      setShowAddUnitDialog(false);
      setNewUnit({
        label: '',
        unitType: 'unit',
        segmentId: undefined,
        description: '',
      });
    } catch (err) {
      console.error('Error adding unit:', err);
      toast({
        title: 'Error',
        description: 'Failed to add unit',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle add segment
  const handleAddSegment = async () => {
    if (!newSegment.name.trim()) {
      toast({
        title: 'Error',
        description: 'Segment name is required',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await createSegment({
        name: newSegment.name,
        segmentType: newSegment.segmentType,
        description: newSegment.description || undefined,
        unitCount: 0,
        status: 'pending',
        sortOrder: segments.length,
      });
      toast({
        title: 'Segment added',
        description: `${newSegment.name} has been added`,
      });
      setShowAddSegmentDialog(false);
      setNewSegment({
        name: '',
        segmentType: 'phase',
        description: '',
      });
    } catch (err) {
      console.error('Error adding segment:', err);
      toast({
        title: 'Error',
        description: 'Failed to add segment',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete unit
  const handleDeleteUnit = async () => {
    if (!unitToDelete) return;

    setIsSubmitting(true);
    try {
      await deleteUnit(unitToDelete.id);
      toast({
        title: 'Unit deleted',
        description: `${unitToDelete.label} has been removed`,
      });
      setShowDeleteDialog(false);
      setUnitToDelete(null);
    } catch (err) {
      console.error('Error deleting unit:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete unit',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (authLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error state
  if (error || !project) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/projects')}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Projects
        </Button>
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <h2 className="text-xl font-semibold mb-2">Project Not Found</h2>
            <p className="text-muted-foreground">
              {error || "This project doesn't exist or you don't have access."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const TypeIcon = PROJECT_TYPE_ICONS[project.developmentType] || Building2;

  return (
    <div className="container mx-auto py-6 px-4 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/projects')}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Projects
        </Button>

        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-primary/10 rounded-lg">
              <TypeIcon className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{project.name}</h1>
              <div className="flex items-center gap-2 mt-1 text-muted-foreground">
                <span>{PROJECT_TYPE_LABELS[project.developmentType]}</span>
                <span>•</span>
                <span>{project.totalUnits} units</span>
              </div>
              {project.masterAddress && (
                <div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
                  <MapPin className="h-3 w-3" />
                  <span>
                    {project.masterAddress.street}, {project.masterAddress.city},{' '}
                    {project.masterAddress.state} {project.masterAddress.zip}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={STATUS_COLORS[project.status]}>
              {project.status.replace('_', ' ')}
            </Badge>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Project Progress</span>
            <span className="text-sm text-muted-foreground">{progressPercent}%</span>
          </div>
          <Progress value={progressPercent} className="h-2" />
          <div className="flex items-center gap-6 mt-3 text-sm">
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span>{project.unitStats?.ready || 0} ready</span>
            </div>
            <div className="flex items-center gap-1">
              <AlertCircle className="h-4 w-4 text-yellow-500" />
              <span>{project.unitStats?.blocked || 0} blocked</span>
            </div>
            <div className="flex items-center gap-1">
              <FileText className="h-4 w-4 text-blue-500" />
              <span>{project.unitStats?.submitted || 0} submitted</span>
            </div>
            <div className="flex items-center gap-1">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>{project.unitStats?.issued || 0} issued</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4 flex-wrap">
          <TabsTrigger value="overview" className="gap-2">
            <Building2 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="units" className="gap-2">
            <Grid3X3 className="h-4 w-4" />
            Units ({units.length})
          </TabsTrigger>
          <TabsTrigger value="jobs" className="gap-2">
            <Briefcase className="h-4 w-4" />
            Jobs ({jobs.length})
          </TabsTrigger>
          <TabsTrigger value="documents" className="gap-2">
            <FolderOpen className="h-4 w-4" />
            Documents
          </TabsTrigger>
          <TabsTrigger value="timeline" className="gap-2">
            <CalendarDays className="h-4 w-4" />
            Timeline
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="h-4 w-4" />
            Settings
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="space-y-6">
            {/* Stats Grid */}
            <ProjectStatsGrid
              totalUnits={project.totalUnits}
              unitStats={project.unitStats || { total: 0, ready: 0, blocked: 0, submitted: 0, issued: 0 }}
              sharedPermitStats={project.sharedPermitStats || { total: 0, ready: 0, blocked: 0, submitted: 0, issued: 0 }}
              totalJobs={jobs.length}
            />

            {/* Blocked Items Alert */}
            <NextBlockedItems
              units={units}
              jobs={jobs}
              projectId={project.id}
            />

            {/* Jurisdiction-Specific Process Steps & Alerts */}
            <DevelopmentProcessSteps
              projectTypeCode={project.developmentType}
              jurisdictionId={project.jurisdictionId}
              jurisdictionCode={project.jurisdictionCode}
            />

            <div className="grid md:grid-cols-2 gap-6">
            {/* Project Info */}
            <Card>
              <CardHeader>
                <CardTitle>Project Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-muted-foreground">Development Type</Label>
                  <p className="font-medium">{PROJECT_TYPE_LABELS[project.developmentType]}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Property Type</Label>
                  <p className="font-medium capitalize">{project.propertyType}</p>
                </div>
                {project.description && (
                  <div>
                    <Label className="text-muted-foreground">Description</Label>
                    <p className="font-medium">{project.description}</p>
                  </div>
                )}
                {project.jurisdictionName && (
                  <div>
                    <Label className="text-muted-foreground">Jurisdiction</Label>
                    <p className="font-medium">{project.jurisdictionName}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Owner Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Owner/Developer
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {project.ownerName && (
                  <div>
                    <Label className="text-muted-foreground">Name</Label>
                    <p className="font-medium">{project.ownerName}</p>
                  </div>
                )}
                {project.ownerCompany && (
                  <div>
                    <Label className="text-muted-foreground">Company</Label>
                    <p className="font-medium">{project.ownerCompany}</p>
                  </div>
                )}
                {project.ownerPhone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{project.ownerPhone}</span>
                  </div>
                )}
                {project.ownerEmail && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{project.ownerEmail}</span>
                  </div>
                )}
                {!project.ownerName && !project.ownerCompany && (
                  <p className="text-muted-foreground">No owner information added</p>
                )}
              </CardContent>
            </Card>

            {/* Segments */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Segments</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddSegmentDialog(true)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </CardHeader>
              <CardContent>
                {segments.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    No segments defined. Add phases, buildings, or blocks to organize units.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {segments.map((segment) => (
                      <div
                        key={segment.id}
                        className="flex items-center justify-between p-2 bg-muted/50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium">{segment.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {segment.segmentType} • {segment.unitCount} units
                          </p>
                        </div>
                        <Badge
                          variant={segment.status === 'complete' ? 'default' : 'secondary'}
                        >
                          {segment.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Shared Permits */}
            <Card>
              <CardHeader>
                <CardTitle>Shared Permits</CardTitle>
                <CardDescription>
                  Project-wide permits (site development, stormwater, etc.)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {sharedJobs.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    No shared permits linked to this project yet.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {sharedJobs.map((job) => (
                      <div
                        key={job.id}
                        className="flex items-center justify-between p-2 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted"
                        onClick={() => navigate(`/jobs/${job.id}`)}
                      >
                        <div>
                          <p className="font-medium">{job.title}</p>
                          <p className="text-sm text-muted-foreground">
                            {job.permitType || 'General'}
                          </p>
                        </div>
                        <Badge variant="secondary">{job.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            </div>
          </div>
        </TabsContent>

        {/* Units Tab */}
        <TabsContent value="units">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle>Units</CardTitle>
                  <CardDescription>
                    Individual lots, units, or buildings in this project
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={unitStatusFilter}
                    onValueChange={(value) => setUnitStatusFilter(value as UnitStatus | 'all')}
                  >
                    <SelectTrigger className="w-[130px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="permitting">Permitting</SelectItem>
                      <SelectItem value="construction">Construction</SelectItem>
                      <SelectItem value="complete">Complete</SelectItem>
                    </SelectContent>
                  </Select>
                  {segments.length > 0 && (
                    <Select
                      value={unitSegmentFilter}
                      onValueChange={setUnitSegmentFilter}
                    >
                      <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="Segment" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Segments</SelectItem>
                        {segments.map((seg) => (
                          <SelectItem key={seg.id} value={seg.id}>
                            {seg.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Button onClick={() => setShowAddUnitDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Unit
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {units.length === 0 ? (
                <div className="text-center py-8">
                  <Grid3X3 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-medium mb-2">No Units Yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Add individual units to track permits for each lot, building, or space.
                  </p>
                  <Button onClick={() => setShowAddUnitDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Unit
                  </Button>
                </div>
              ) : filteredUnits.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">
                    No units match the current filters.
                  </p>
                  <Button
                    variant="link"
                    onClick={() => {
                      setUnitStatusFilter('all');
                      setUnitSegmentFilter('all');
                    }}
                  >
                    Clear filters
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Showing {filteredUnits.length} of {units.length} units
                  </p>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {filteredUnits.map((unit) => {
                    const unitJobsList = unitJobs.get(unit.id) || [];
                    return (
                      <Card
                        key={unit.id}
                        className="cursor-pointer hover:shadow-md transition-shadow"
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-medium">{unit.label}</h4>
                              <p className="text-sm text-muted-foreground">
                                {UNIT_TYPE_LABELS[unit.unitType]}
                              </p>
                            </div>
                            <Badge className={UNIT_STATUS_COLORS[unit.status]}>
                              {unit.status}
                            </Badge>
                          </div>
                          {unit.address && (
                            <p className="text-sm text-muted-foreground mt-2">
                              {unit.address.street}
                            </p>
                          )}
                          <div className="flex items-center justify-between mt-3 pt-3 border-t">
                            <span className="text-sm text-muted-foreground">
                              {unitJobsList.length} job{unitJobsList.length !== 1 ? 's' : ''}
                            </span>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setUnitToDelete(unit);
                                  setShowDeleteDialog(true);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Jobs Tab */}
        <TabsContent value="jobs">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Linked Jobs</CardTitle>
                <CardDescription>
                  All permit jobs associated with this project
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => navigate('/jobs/new')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Single Job
                </Button>
                <Button onClick={() => setShowAddJobsWizard(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Batch Add Jobs
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {jobsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : jobs.length === 0 ? (
                <div className="text-center py-8">
                  <Briefcase className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-medium mb-2">No Jobs Yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Create permit jobs and link them to this project.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Shared Jobs Section */}
                  {sharedJobs.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2 text-sm text-muted-foreground uppercase tracking-wider">
                        Shared/Site-Wide Permits
                      </h4>
                      <div className="space-y-2">
                        {sharedJobs.map((job) => (
                          <div
                            key={job.id}
                            className={`flex items-center justify-between p-3 rounded-lg cursor-pointer hover:bg-muted transition-colors ${
                              job.complianceStatus === 'fail' ? 'bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-900' : 'bg-muted/50'
                            }`}
                            onClick={() => navigate(`/jobs/${job.id}`)}
                          >
                            <div>
                              <p className="font-medium">{job.title}</p>
                              <p className="text-sm text-muted-foreground">
                                {job.jobScopeType === 'shared_common' ? 'Shared Common' : 'Campus/Site'}
                                {job.permitType && ` • ${job.permitType}`}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              {job.complianceStatus === 'fail' && (
                                <Badge variant="destructive" className="text-xs">Blocked</Badge>
                              )}
                              {job.complianceStatus === 'pass' && (
                                <Badge className="bg-green-100 text-green-800 text-xs">Ready</Badge>
                              )}
                              <Badge variant="secondary">{job.status}</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Unit-Specific Jobs Section */}
                  {Array.from(unitJobs.entries()).map(([unitId, unitJobsList]) => {
                    const unit = units.find((u) => u.id === unitId);
                    if (!unit || unitJobsList.length === 0) return null;
                    return (
                      <div key={unitId}>
                        <h4 className="font-medium mb-2 text-sm text-muted-foreground uppercase tracking-wider">
                          {unit.label}
                        </h4>
                        <div className="space-y-2">
                          {unitJobsList.map((job) => (
                            <div
                              key={job.id}
                              className={`flex items-center justify-between p-3 rounded-lg cursor-pointer hover:bg-muted transition-colors ${
                                job.complianceStatus === 'fail' ? 'bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-900' : 'bg-muted/50'
                              }`}
                              onClick={() => navigate(`/jobs/${job.id}`)}
                            >
                              <div>
                                <p className="font-medium">{job.title}</p>
                                <p className="text-sm text-muted-foreground">
                                  {job.permitType || 'General'}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                {job.complianceStatus === 'fail' && (
                                  <Badge variant="destructive" className="text-xs">Blocked</Badge>
                                )}
                                {job.complianceStatus === 'pass' && (
                                  <Badge className="bg-green-100 text-green-800 text-xs">Ready</Badge>
                                )}
                                <Badge variant="secondary">{job.status}</Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <ProjectDocumentLibrary
            projectId={project.id}
            units={units}
            segments={segments}
          />
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline">
          <div className="space-y-6">
            <ProjectTimeline
              projectId={project.id}
              units={units}
              segments={segments}
              milestones={[]}
              isLoading={false}
            />
            <BulkComplianceCheck
              projectId={project.id}
              totalJobs={jobs.length}
            />
          </div>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Project Settings</CardTitle>
              <CardDescription>
                Manage project configuration and metadata
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium mb-2">Project ID</h4>
                <code className="text-sm text-muted-foreground">{project.id}</code>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium mb-2">Created</h4>
                <p className="text-sm text-muted-foreground">
                  {project.createdAt
                    ? new Date(
                        'toDate' in project.createdAt
                          ? (project.createdAt as any).toDate()
                          : project.createdAt
                      ).toLocaleDateString()
                    : 'Unknown'}
                </p>
              </div>
              {/* Future: Add edit functionality, status updates, etc. */}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Unit Dialog */}
      <Dialog open={showAddUnitDialog} onOpenChange={setShowAddUnitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Unit</DialogTitle>
            <DialogDescription>
              Add a new unit (lot, apartment, townhome, etc.) to this project.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="unit-label">Label *</Label>
              <Input
                id="unit-label"
                placeholder="e.g., Lot 12, Unit 305, Building A"
                value={newUnit.label}
                onChange={(e) =>
                  setNewUnit((prev) => ({ ...prev, label: e.target.value }))
                }
              />
            </div>
            <div>
              <Label htmlFor="unit-type">Unit Type</Label>
              <Select
                value={newUnit.unitType}
                onValueChange={(value: UnitType) =>
                  setNewUnit((prev) => ({ ...prev, unitType: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lot">Lot</SelectItem>
                  <SelectItem value="unit">Unit</SelectItem>
                  <SelectItem value="building">Building</SelectItem>
                  <SelectItem value="townhome">Townhome</SelectItem>
                  <SelectItem value="apartment">Apartment</SelectItem>
                  <SelectItem value="space">Space</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {segments.length > 0 && (
              <div>
                <Label htmlFor="unit-segment">Segment (Optional)</Label>
                <Select
                  value={newUnit.segmentId || 'none'}
                  onValueChange={(value) =>
                    setNewUnit((prev) => ({
                      ...prev,
                      segmentId: value === 'none' ? undefined : value,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a segment" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No segment</SelectItem>
                    {segments.map((segment) => (
                      <SelectItem key={segment.id} value={segment.id}>
                        {segment.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label htmlFor="unit-description">Description (Optional)</Label>
              <Input
                id="unit-description"
                placeholder="Optional notes about this unit"
                value={newUnit.description || ''}
                onChange={(e) =>
                  setNewUnit((prev) => ({ ...prev, description: e.target.value }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddUnitDialog(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={handleAddUnit} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Unit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Segment Dialog */}
      <Dialog open={showAddSegmentDialog} onOpenChange={setShowAddSegmentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Segment</DialogTitle>
            <DialogDescription>
              Add a segment to organize units (e.g., Phase 1, Building A, Block 2).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="segment-name">Name *</Label>
              <Input
                id="segment-name"
                placeholder="e.g., Phase 1, Building A, Block 2"
                value={newSegment.name}
                onChange={(e) =>
                  setNewSegment((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </div>
            <div>
              <Label htmlFor="segment-type">Type</Label>
              <Select
                value={newSegment.segmentType}
                onValueChange={(value: SegmentType) =>
                  setNewSegment((prev) => ({ ...prev, segmentType: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="phase">Phase</SelectItem>
                  <SelectItem value="building">Building</SelectItem>
                  <SelectItem value="block">Block</SelectItem>
                  <SelectItem value="section">Section</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="segment-description">Description (Optional)</Label>
              <Input
                id="segment-description"
                placeholder="Optional notes about this segment"
                value={newSegment.description}
                onChange={(e) =>
                  setNewSegment((prev) => ({ ...prev, description: e.target.value }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddSegmentDialog(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={handleAddSegment} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Segment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Unit Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Unit</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{unitToDelete?.label}"? This action
              cannot be undone. Any jobs linked to this unit will be unlinked.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUnit}
              disabled={isSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Jobs Wizard Dialog */}
      <Dialog open={showAddJobsWizard} onOpenChange={setShowAddJobsWizard}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <AddJobsWizard
            projectId={project.id}
            projectName={project.name}
            units={units}
            onComplete={(jobIds) => {
              setShowAddJobsWizard(false);
              refreshJobs();
              toast({
                title: 'Jobs created',
                description: `Successfully created ${jobIds.length} jobs`,
              });
            }}
            onCancel={() => setShowAddJobsWizard(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectDetail;
